import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import AdminAuthGate from "./AdminAuthGate";
import RoleSidebar from "../shared/RoleSidebar";
import NotificationToolbarBell from "../shared/NotificationToolbarBell";
import AppBottomDock from "../shared/AppBottomDock";
import OpsAdminSearchModal from "./OpsAdminSearchModal";
import { initialSidebarExpanded } from "../../utils/layoutSidebar";
import api from "../../services/api";
import { pickOpsField } from "../../utils/opsApiPayload";
import { isOpsAuthRole } from "../../utils/routeAccess";
import "./OpsLayout.css";
import "./OpsAdminSearchModal.css";

function useIsDesktopLayout() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 901px)").matches : true
  );

  useEffect(() => {
    const media = window.matchMedia("(min-width: 901px)");
    function onChange(event) {
      setIsDesktop(event.matches);
    }
    setIsDesktop(media.matches);
    if (media.addEventListener) {
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  return isDesktop;
}

function OpsLayout() {
  const isDesktop = useIsDesktopLayout();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(initialSidebarExpanded);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const token = api.getToken?.();
    const authRole = api.getAuthRoleFromToken?.();
    if (!token || !isOpsAuthRole(authRole)) return undefined;

    let cancelled = false;
    api
      .getOpsAdminConsole()
      .then((res) => {
        if (cancelled) return;
        const consoleDoc = pickOpsField(res, "console", {});
        const system = consoleDoc?.system || {};
        document.documentElement.classList.toggle("aps-admin-dark", Boolean(system.darkMode));
        const pageSize = Number(system.pageSize);
        if (pageSize > 0) {
          try {
            window.localStorage.setItem("nema_admin_table_page_size", String(pageSize));
          } catch {
            /* ignore */
          }
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    function onKeyDown(event) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  function handleMenuToggle() {
    if (isDesktop) {
      setSidebarExpanded((s) => !s);
      return;
    }
    setSidebarOpen((s) => !s);
  }

  return (
    <AdminAuthGate>
    <div className={`ops-layout ${sidebarExpanded ? "ops-layout--expanded" : "ops-layout--compact"}`}>
      <button
        type="button"
        className="layout-hamburger"
        aria-label="Open menu"
        onClick={handleMenuToggle}
      >
        ☰
      </button>
      {sidebarOpen ? (
        <button
          type="button"
          className="layout-overlay"
          aria-label="Close menu overlay"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}
      <div className="ops-layout__shell">
        <RoleSidebar
          role="ops"
          isOpen={sidebarOpen}
          isExpanded={sidebarExpanded}
          onClose={() => setSidebarOpen(false)}
          onToggleExpand={handleMenuToggle}
        />
        <div className="ops-layout__main">
          <div className="ops-layout__toolbar">
            <button
              type="button"
              className="ops-layout__search-trigger"
              aria-label="Open dashboard search"
              onClick={() => setSearchOpen(true)}
            >
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <circle cx="9" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.6" />
                <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
              <span className="ops-layout__search-trigger-label">Search anything...</span>
              <kbd>Ctrl + K</kbd>
            </button>
            <div className="ops-layout__toolbar-actions">
              <NotificationToolbarBell />
            </div>
          </div>
          <OpsAdminSearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
          <Outlet />
        </div>
      </div>
      <AppBottomDock />
    </div>
    </AdminAuthGate>
  );
}

export default OpsLayout;
